import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { toast } from 'sonner';
import { ChevronsUpDown, Check, Users, X, Trash2, AlertTriangle } from 'lucide-react';
import FieldEventAttachments from './FieldEventAttachments';
import EquipmentFields from '../job/EquipmentFields';
import useEquipmentCatalog from '../../hooks/useEquipmentCatalog';
import DateField from '../DateField';
import { RecordSiteFields } from '../customer/RecordSiteFields';
import { recordSite, recordSitePatch } from '../../utils/siteLocation';
import { extractErrorMessage } from '../../utils/apiErrors';
import { FieldEventCustomerPicker } from './FieldEventCustomerPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NONE = '__none__';

const blank = (defaultDate, calId, allDay = false) => ({
  title: '', job_number: '', date: defaultDate || new Date().toISOString().slice(0, 10),
  end_date: defaultDate || new Date().toISOString().slice(0, 10),
  all_day: allDay, start_time: '08:00', end_time: '17:00',
  calendar_id: calId || '', category_id: null, location: '', notes: '',
  attendees: [], customer_id: null, repeat_rule: 'none', repeat_until: '',
  lsd: '', site_details: null, equipment: [],
});

export default function FieldEventDialog({ open, onClose, onSaved, event, defaultDate, calendars, activeCalId, users, customers, canAssign, onCustomersChanged, defaultAllDay = false, onDelete, dark }) {
  const { types: equipmentTypes, refresh: refreshCatalog } = useEquipmentCatalog();
  const [form, setForm] = useState(blank(defaultDate, activeCalId, defaultAllDay));
  const [dialogCategories, setDialogCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attendeePickerOpen, setAttendeePickerOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [jobMatch, setJobMatch] = useState(null);
  const [certAlerts, setCertAlerts] = useState({});
  const isEdit = Boolean(event?.id);
  const attendeeOptions = users.filter((u) => u.field_access !== false);

  useEffect(() => {
    if (!open) return;
    setConfirmDelete(false);
    setPendingFiles([]);
    if (event) {
      setForm({
        title: event.title || '', job_number: event.job_number || '',
        date: event.date, end_date: event.end_date || event.date,
        all_day: !!event.all_day,
        start_time: event.all_day ? '08:00' : (event.start_time || '08:00'),
        end_time: event.all_day ? '17:00' : (event.end_time || '17:00'),
        calendar_id: event.calendar_id || activeCalId, category_id: event.category_id || null,
        location: event.location || '', lsd: event.lsd || '', notes: event.notes || '',
        site_details: event.site_details ?? null,
        attendees: event.attendees || [], customer_id: event.customer_id || null,
        equipment: event.equipment || [],
        repeat_rule: 'none', repeat_until: '',
      });
    } else {
      setForm(blank(defaultDate, activeCalId, defaultAllDay));
    }
  }, [open, event, defaultDate, activeCalId, defaultAllDay]);

  useEffect(() => {
    if (!open || !form.calendar_id) return;
    axios.get(`${API}/field/categories/${form.calendar_id}`).then((r) => setDialogCategories(r.data)).catch(() => setDialogCategories([]));
  }, [open, form.calendar_id]);

  useEffect(() => {
    if (!open) return;
    const jn = form.job_number.trim();
    if (!jn) { setJobMatch(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/field/job-lookup`, { params: { job_number: jn } })
        .then((r) => {
          setJobMatch(r.data);
          if (r.data.found && r.data.customer_id) {
            setForm((f) => (f.customer_id ? f : { ...f, customer_id: r.data.customer_id }));
          }
        })
        .catch(() => setJobMatch(null));
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.job_number]);

  useEffect(() => {
    if (!open || !canAssign) return;
    // Soft compliance check — lets dispatch see lapsed tickets before booking a tech.
    axios.get(`${API}/safety-docs/user-alerts`)
      .then((r) => setCertAlerts(r.data || {}))
      .catch(() => setCertAlerts({}));
  }, [open, canAssign]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleAttendee = (id) => {
    set({ attendees: form.attendees.includes(id) ? form.attendees.filter((a) => a !== id) : [...form.attendees, id] });
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (form.end_date < form.date) return toast.error('End date cannot be before start date');
    setSaving(true);
    const payload = { ...form, repeat_until: form.repeat_until || null };
    try {
      if (isEdit) {
        await axios.put(`${API}/field/events/${event.id}`, payload);
        toast.success('Event updated');
      } else {
        const res = await axios.post(`${API}/field/events`, payload);
        const n = res.data.events?.length || 1;
        const created = res.data.events?.[0] || res.data.event;
        if (pendingFiles.length && created?.id) {
          for (const f of pendingFiles) {
            const fd = new FormData();
            fd.append('file', f);
            await axios.post(`${API}/field/events/${created.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          }
        }
        toast.success(n > 1 ? `${n} events created` : 'Event created');
      }
      await refreshCatalog();
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to save event'));
    } finally {
      setSaving(false);
    }
  };

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const sites = selectedCustomer?.sites || [];

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={`grid-cols-[minmax(0,1fr)] max-h-[90vh] overflow-y-auto overflow-x-hidden text-[15px] [&_label]:text-[13.5px] ${dark ? 'dark bg-slate-900 border-slate-700 text-slate-100' : ''}`}
        style={{ width: 'min(95vw, 34rem)', maxWidth: 'min(95vw, 34rem)' }}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="field-event-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Event' : 'New Event'}</DialogTitle>
          <DialogDescription>Field dispatch calendar event</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Pump service — north site" data-testid="field-event-title-input" />
          </div>
          <FieldEventCustomerPicker customers={customers} value={form.customer_id} dark={dark}
            onChange={(customer_id) => set(customer_id === form.customer_id ? { customer_id } : { customer_id, location: '', lsd: '', site_details: null })} />
          <RecordSiteFields value={recordSite(form, sites)} onChange={(site) => set(recordSitePatch(site))}
            customer={selectedCustomer} onCustomerUpdated={onCustomersChanged} testPrefix="field-event-site" dark={dark} />
          <div>
            <Label>Job #</Label>
            <Input value={form.job_number} onChange={(e) => set({ job_number: e.target.value })} data-testid="field-event-jobnumber-input" />
            {form.job_number.trim() && jobMatch?.found && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-testid="field-event-job-linked-hint">
                ✓ Linked to shop job #{jobMatch.job_number}{jobMatch.customer_name ? ` — ${jobMatch.customer_name}` : ''}
              </p>
            )}
            {form.job_number.trim() && jobMatch && !jobMatch.found && (
              <p className="text-xs text-slate-400 mt-1" data-testid="field-event-job-nomatch-hint">
                No shop job with this number yet — it will link automatically if one is created
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <DateField dark={dark} value={form.date} onChange={(v) => set({ date: v, end_date: form.end_date < v ? v : form.end_date })} data-testid="field-event-date-input" />
            </div>
            <div>
              <Label>End date</Label>
              <DateField dark={dark} value={form.end_date} min={form.date} onChange={(v) => set({ end_date: v })} data-testid="field-event-enddate-input" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.all_day} onCheckedChange={(v) => set({ all_day: v })} data-testid="field-event-allday-switch" />
            <Label className="cursor-pointer" onClick={() => set({ all_day: !form.all_day })}>All day</Label>
          </div>

          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start time</Label>
                <Input type="time" value={form.start_time} onChange={(e) => set({ start_time: e.target.value })} data-testid="field-event-starttime-input" />
              </div>
              <div>
                <Label>End time</Label>
                <Input type="time" value={form.end_time} onChange={(e) => set({ end_time: e.target.value })} data-testid="field-event-endtime-input" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Calendar</Label>
              <Select value={form.calendar_id} onValueChange={(v) => set({ calendar_id: v, category_id: null })}>
                <SelectTrigger data-testid="field-event-calendar-select"><SelectValue placeholder="Calendar" /></SelectTrigger>
                <SelectContent className={dark ? 'dark' : ''}>
                  {calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category_id || NONE} onValueChange={(v) => set({ category_id: v === NONE ? null : v })}>
                <SelectTrigger data-testid="field-event-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent className={dark ? 'dark' : ''}>
                  <SelectItem value={NONE}>No category</SelectItem>
                  {dialogCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                        {c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {canAssign && (
            <div>
              <div className="flex items-start gap-2 flex-wrap">
                <Label className="flex items-center gap-1.5 pt-0.5"><Users size={13} /> Attendees ({form.attendees.length})</Label>
                {form.attendees.length > 0 && (
                  <div className="flex flex-wrap gap-1" data-testid="field-event-selected-attendees">
                    {form.attendees.map((id) => {
                      const u = users.find((x) => x.id === id);
                      return (
                        <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200">
                          {u?.name || 'Unknown'}
                          <button type="button" onClick={() => toggleAttendee(id)} className="hover:text-red-600 dark:hover:text-red-400" aria-label={`Remove ${u?.name || 'attendee'}`}>
                            <X size={11} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <Popover open={attendeePickerOpen} onOpenChange={setAttendeePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-1 w-full justify-between font-normal"
                    data-testid="field-event-attendees-trigger"
                  >
                    <span className="truncate text-slate-500 dark:text-slate-400">
                      {form.attendees.length ? `${form.attendees.length} selected — search to add or remove` : 'Search technicians...'}
                    </span>
                    <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-[--radix-popover-trigger-width] p-0 z-[120] ${dark ? 'dark' : ''}`} align="start">
                  <Command>
                    <CommandInput placeholder="Search by name or role..." data-testid="field-event-attendees-search" />
                    <CommandList data-testid="field-event-attendees-list">
                      <CommandEmpty>
                        {attendeeOptions.length === 0 ? 'No users with field calendar access.' : 'No matching technicians.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {attendeeOptions.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={`${u.name} ${u.role || ''}`}
                            onSelect={() => toggleAttendee(u.id)}
                            data-testid={`field-event-attendee-option-${u.id}`}
                          >
                            <Checkbox checked={form.attendees.includes(u.id)} className="mr-2 pointer-events-none" />
                            <span className="flex-1 truncate">{u.name}</span>
                            {!!certAlerts[u.id] && (
                              <AlertTriangle
                                size={12}
                                className={(certAlerts[u.id].expired || []).length ? 'ml-1 text-red-500' : 'ml-1 text-amber-500'}
                                data-testid={`field-event-attendee-cert-flag-${u.id}`}
                              />
                            )}
                            <span className="ml-2 text-[10px] uppercase text-slate-400">{u.role?.replace(/_/g, ' ')}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {(() => {
                const flagged = form.attendees
                  .map((id) => ({ id, alert: certAlerts[id] }))
                  .filter((a) => a.alert && ((a.alert.expired || []).length || (a.alert.expiring_soon || []).length));
                if (!flagged.length) return null;
                return (
                  <div
                    className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
                    data-testid="field-event-cert-warning"
                  >
                    <div className="mb-1 flex items-center gap-1.5 font-bold">
                      <AlertTriangle size={13} /> Safety docs need attention
                    </div>
                    {flagged.map(({ id, alert }) => {
                      const name = users.find((x) => x.id === id)?.name || 'Attendee';
                      const items = [
                        ...(alert.expired || []).map((d) => `${d.title} expired ${d.expiry_date}`),
                        ...(alert.expiring_soon || []).map((d) => `${d.title} expires in ${d.days_remaining}d`),
                      ];
                      return (
                        <div key={id} data-testid={`field-event-cert-warning-${id}`}>
                          <span className="font-semibold">{name}:</span> {items.join(' · ')}
                        </div>
                      );
                    })}
                    <div className="mt-1 opacity-80">You can still schedule them — this is a reminder only.</div>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
            <EquipmentFields
              value={form.equipment}
              onChange={(equipment) => set({ equipment })}
              types={equipmentTypes}
              customerId={form.customer_id || null}
              customer={selectedCustomer}
              site={recordSite(form, sites)}
              onReuseSite={(site, row) => setForm((prev) => ({ ...prev,
                ...(!prev.customer_id && row.customer_id ? { customer_id: row.customer_id } : {}),
                ...recordSitePatch(site) }))}
              dark={dark}
              testPrefix="field-event-equipment"
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={6} data-testid="field-event-notes-input" />
          </div>

          {isEdit && event?.id ? (
            <FieldEventAttachments eventId={event.id} canEdit dark={dark} equipment={event.equipment || []} />
          ) : (
            <FieldEventAttachments canEdit dark={dark} pendingFiles={pendingFiles} onPendingChange={setPendingFiles} />
          )}

          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Repeat</Label>
                <Select value={form.repeat_rule} onValueChange={(v) => set({ repeat_rule: v })}>
                  <SelectTrigger data-testid="field-event-repeat-select"><SelectValue /></SelectTrigger>
                  <SelectContent className={dark ? 'dark' : ''}>
                    <SelectItem value="none">Does not repeat</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.repeat_rule !== 'none' && (
                <div>
                  <Label>Repeat until</Label>
                  <DateField dark={dark} value={form.repeat_until} min={form.date} onChange={(v) => set({ repeat_until: v })} data-testid="field-event-repeatuntil-input" />
                </div>
              )}
            </div>
          )}

          {confirmDelete && (
            <div
              ref={(el) => el?.scrollIntoView({ block: 'nearest' })}
              className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40"
              data-testid="field-event-delete-confirm-panel"
            >
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">Delete this event?</p>
              <p className="mt-1 text-[13px] text-red-700/80 dark:text-red-300/80 break-words">
                "{form.title || event?.title}" will be moved to the recycle bin. It can be restored by a schedule manager within 90 days.
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setConfirmDelete(false)} data-testid="field-event-delete-cancel">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => { const ev = event; setConfirmDelete(false); onDelete(ev); }}
                  data-testid="field-event-delete-confirm"
                >
                  <Trash2 size={13} className="mr-1.5" /> Delete Event
                </Button>
              </div>
            </div>
          )}

          <div className={`sticky -bottom-6 -mx-6 -mb-6 mt-1 flex items-center gap-2 border-t px-6 py-3 ${dark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
            {isEdit && onDelete && (
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950"
                onClick={() => setConfirmDelete(true)}
                data-testid="field-event-delete-btn"
              >
                <Trash2 size={13} className="mr-1.5" /> Delete
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={save} disabled={saving} data-testid="field-event-save-btn">
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Event'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
